/**
 * /sources — "Connected Signal accounts": manage linked BYO Signal accounts
 * (plan signal-connector.md §5.6). Per account: status; per indexed chat:
 * visibility and ingestion.
 *
 * Stopping the account lives on the View page (/sources/signal/:id) — it needs
 * a fresh QR scan to undo, which is too costly to sit one mis-click away in a
 * list. See connector-danger-zone.tsx.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Download, Lock, Send, User, Users } from 'lucide-react';
import { toast } from 'sonner';
import { IconTile } from '@/components/patterns/icon-tile';
import { StatusPill, type StatusPillTone } from '@/components/patterns/status-pill';
import { buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  connectorApi,
  type SignalAccount,
  type SignalAccountStatus,
  type SignalSelectedChat,
  signalAccountApi,
} from '@/lib/api';
import { qk } from '@/lib/query-client';
import { cn } from '@/lib/utils';

const STATUS: Record<SignalAccountStatus, { label: string; tone: StatusPillTone }> = {
  pending_link: { label: 'Connecting', tone: 'neutral' },
  linking: { label: 'Waiting for scan', tone: 'neutral' },
  working: { label: 'Connected', tone: 'active' },
  unlinked: { label: 'Disconnected', tone: 'paused' },
  error: { label: 'Error', tone: 'danger' },
};

function IngestSwitch({
  on,
  disabled,
  onClick,
}: {
  on: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={on ? 'Disable ingestion' : 'Enable ingestion'}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'relative h-6 w-[42px] shrink-0 rounded-full transition-colors disabled:opacity-50',
        on ? 'bg-primary' : 'bg-[#e2e2e5]',
      )}
    >
      <span
        className={cn(
          'absolute top-1/2 left-[3px] h-[18px] w-[18px] -translate-y-1/2 rounded-full bg-white transition-transform',
          on && 'translate-x-[18px]',
        )}
      />
    </button>
  );
}

function ChatRow({ accountId, chat }: { accountId: string; chat: SignalSelectedChat }) {
  const qc = useQueryClient();
  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: [...qk.signalAccount(accountId), 'selected'] });

  const visMut = useMutation({
    mutationFn: (v: 'workspace' | 'private') =>
      connectorApi.setCollectionVisibility(chat.collectionId, v),
    onSuccess: invalidate,
    onError: () => toast.error('Failed to update visibility'),
  });
  const ingMut = useMutation({
    mutationFn: (enabled: boolean) => connectorApi.patchCollection(chat.collectionId, enabled),
    onSuccess: invalidate,
    onError: () => toast.error('Failed to update ingestion'),
  });

  return (
    <div className="flex items-center gap-3 border-border-subtle border-b px-4 py-3 last:border-b-0">
      {chat.kind === 'group' ? (
        <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
      ) : (
        <User className="h-4 w-4 shrink-0 text-muted-foreground" />
      )}
      <span className="min-w-0 flex-1 truncate text-[14px]">{chat.title}</span>

      {/* Visibility. A DM is created private and stays private unless the owner
          deliberately shares it here — this control IS that deliberate action. */}
      <div className="inline-flex overflow-hidden rounded-md border border-border text-[11.5px]">
        {(['workspace', 'private'] as const).map((v) => (
          <button
            key={v}
            type="button"
            disabled={visMut.isPending}
            onClick={() => chat.visibility !== v && visMut.mutate(v)}
            className={cn(
              'px-2 py-1 transition-colors',
              chat.visibility === v
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {v === 'workspace' ? 'Shared' : 'Private'}
          </button>
        ))}
      </div>
      {chat.visibility === 'private' && <Lock className="h-3 w-3 text-faint" />}

      <StatusPill tone={chat.ingestionEnabled ? 'active' : 'paused'}>
        {chat.ingestionEnabled ? 'Indexing' : 'Paused'}
      </StatusPill>
      <Link
        to="/sources/collection/$collectionId"
        params={{ collectionId: chat.collectionId }}
        className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'shrink-0')}
      >
        View
      </Link>
      <IngestSwitch
        on={chat.ingestionEnabled}
        disabled={ingMut.isPending}
        onClick={() => ingMut.mutate(!chat.ingestionEnabled)}
      />
    </div>
  );
}

function AccountCard({ account }: { account: SignalAccount }) {
  const { data: chats, isLoading } = useQuery({
    queryKey: [...qk.signalAccount(account.id), 'selected'],
    queryFn: () => signalAccountApi.selectedChats(account.id),
    enabled: account.status === 'working',
  });

  const status = STATUS[account.status];

  return (
    <div className="rounded-xl border border-border p-6">
      <div className="flex items-center gap-4">
        <IconTile tone="green">
          <Send className="h-5 w-5" />
        </IconTile>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-[15px]">{account.e164 ?? 'Your Signal'}</p>
          <p className="truncate font-mono text-[13px] text-faint">
            Linked {new Date(account.createdAt).toLocaleDateString()}
            {account.deviceName ? ` · ${account.deviceName}` : ''}
          </p>
        </div>
        <StatusPill tone={status.tone}>{status.label}</StatusPill>
        <Link
          to="/sources/$connector/$accountId"
          params={{ connector: 'signal', accountId: account.id }}
          className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'shrink-0')}
        >
          View
        </Link>
      </div>

      {account.status === 'error' && (
        <p className="mt-3 text-[13px] text-destructive">
          Brain lost this connection. Re-link it from onboarding to resume indexing.
        </p>
      )}

      {account.status === 'working' && (
        <>
          <div className="mt-4 overflow-hidden rounded-lg border border-border-subtle">
            {isLoading ? (
              <div className="p-4">
                <Skeleton className="h-5 w-full" />
              </div>
            ) : (chats ?? []).length === 0 ? (
              <p className="p-4 text-[13px] text-faint">
                No chats selected yet — pick some from onboarding to start indexing.
              </p>
            ) : (
              (chats ?? []).map((chat) => (
                <ChatRow key={chat.chatKey} accountId={account.id} chat={chat} />
              ))
            )}
          </div>

          {/* Linking only indexes messages from here on, so the backup import
              is how older conversations arrive. It hangs off the connected
              account because that is the only way in. */}
          <div className="mt-4 flex items-center gap-3 border-border-subtle border-t pt-4">
            <Download className="h-4 w-4 shrink-0 text-faint" />
            <p className="min-w-0 flex-1 text-[13px] text-muted-foreground">
              Search conversations from before you linked Signal by importing a backup.
            </p>
            <Link
              to="/sources/signal/import"
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'shrink-0')}
            >
              Import history
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

export function SignalConnectedAccounts() {
  const { data: accounts } = useQuery({
    queryKey: qk.signalAccounts,
    queryFn: () => signalAccountApi.list(),
    // Signal may be unconfigured (503) — stay quiet rather than retrying.
    retry: false,
  });

  const live = (accounts ?? []).filter((a) => a.status !== 'unlinked');
  if (live.length === 0) return null;

  return (
    <div className="mt-8">
      <h2 className="font-semibold text-base">Connected Signal accounts</h2>
      <p className="mt-1 text-[13px] text-muted-foreground">
        Your linked Signal accounts. Direct messages are private to you unless you share them.
      </p>
      <div className="mt-4 flex flex-col gap-4">
        {live.map((a) => (
          <AccountCard key={a.id} account={a} />
        ))}
      </div>
    </div>
  );
}
